import type { Tone } from '../../ui/v2';
import {
  FxSnapshot,
  NullableNumber,
  SubscriptionRow,
  numberValue,
} from './billingAdminShared';

export type SubscriptionSortKey =
  | 'store'
  | 'plan'
  | 'status'
  | 'monthlyPrice'
  | 'periodEnd'
  | 'minutes'
  | 'cost'
  | 'costPerMinute'
  | 'margin'
  | 'lifetimeRevenue'
  | 'lifetimeCost';

export type SortDirection = 'asc' | 'desc';

export const DEFAULT_SUBSCRIPTION_SORT: {
  key: SubscriptionSortKey;
  direction: SortDirection;
} = { key: 'margin', direction: 'desc' };

export interface SubscriptionFilters {
  search: string;
  status: string;
  plan: string;
}

export const ALL = 'all';

export const subscriptionStatusOptions = (rows: SubscriptionRow[]) =>
  Array.from(
    new Set(rows.map((item) => item.status).filter(Boolean)),
  ).sort() as string[];

/** `[code, name]` pairs, sorted by name. */
export const subscriptionPlanOptions = (rows: SubscriptionRow[]) =>
  Array.from(
    new Map(
      rows
        .filter((item) => item.plan?.code)
        .map((item) => [
          item.plan?.code as string,
          item.plan?.name || item.plan?.code || 'Unknown',
        ]),
    ).entries(),
  ).sort((a, b) => a[1].localeCompare(b[1]));

const sortValue = (
  row: SubscriptionRow,
  key: SubscriptionSortKey,
): string | number => {
  switch (key) {
    case 'store':
      return row.store?.name?.toLowerCase() || '';
    case 'plan':
      return row.plan?.name?.toLowerCase() || '';
    case 'status':
      return row.status || '';
    case 'monthlyPrice':
      return numberValue(row.pricing?.monthly_value_cents);
    case 'periodEnd':
      return row.current_period?.end
        ? new Date(row.current_period.end).getTime()
        : 0;
    case 'minutes':
      return numberValue(row.retell?.current_period?.duration_minutes);
    case 'cost':
      return numberValue(row.current_cost_cents);
    case 'costPerMinute':
      return row.retell_usd_cents_per_minute == null
        ? Number.NEGATIVE_INFINITY
        : numberValue(row.retell_usd_cents_per_minute);
    case 'margin':
      return row.current_margin_percent == null
        ? Number.NEGATIVE_INFINITY
        : row.current_margin_percent;
    case 'lifetimeRevenue':
      return numberValue(row.lifetime_paid_revenue_cents);
    case 'lifetimeCost':
      return numberValue(row.lifetime_cost_cents);
  }
};

export function filterSubscriptions(
  rows: SubscriptionRow[],
  { search, status, plan }: SubscriptionFilters,
): SubscriptionRow[] {
  const query = search.trim().toLowerCase();
  return rows.filter((row) => {
    const matchesSearch =
      !query ||
      [row.store?.name, row.group?.name, row.plan?.name, row.plan?.code]
        .filter(Boolean)
        .some((value) => value?.toLowerCase().includes(query));
    return (
      matchesSearch &&
      (status === ALL || row.status === status) &&
      (plan === ALL || row.plan?.code === plan)
    );
  });
}

export function sortSubscriptions(
  rows: SubscriptionRow[],
  key: SubscriptionSortKey,
  direction: SortDirection,
): SubscriptionRow[] {
  return [...rows].sort((left, right) => {
    const leftValue = sortValue(left, key);
    const rightValue = sortValue(right, key);
    const comparison =
      typeof leftValue === 'string' && typeof rightValue === 'string'
        ? leftValue.localeCompare(rightValue)
        : Number(leftValue) - Number(rightValue);
    return direction === 'asc' ? comparison : -comparison;
  });
}

/** Same column flips; a new text column starts ascending, numbers descending. */
export const nextSort = (
  current: { key: SubscriptionSortKey; direction: SortDirection },
  key: SubscriptionSortKey,
): { key: SubscriptionSortKey; direction: SortDirection } =>
  current.key === key
    ? { key, direction: current.direction === 'asc' ? 'desc' : 'asc' }
    : { key, direction: key === 'store' || key === 'plan' ? 'asc' : 'desc' };

export const subscriptionStatusTone = (status?: string): Tone => {
  switch (status?.toUpperCase()) {
    case 'ACTIVE':
      return 'success';
    case 'PAST_DUE':
      return 'warning';
    case 'CANCELED':
    case 'UNPAID':
      return 'danger';
    default:
      return 'neutral';
  }
};

export const marginTone = (margin: NullableNumber): Tone => {
  if (margin == null) return 'neutral';
  if (margin >= 70) return 'success';
  if (margin >= 40) return 'warning';
  return 'danger';
};

export const fxCaption = (fx?: FxSnapshot | null): string | null => {
  const quotes = Object.entries(fx?.rates ?? {}).filter(
    ([code]) => code !== 'USD',
  );
  if (quotes.length === 0) return null;
  const pairs = quotes
    .map(([code, rate]) => `1 USD = ${rate} ${code}`)
    .join(', ');
  const dated = fx?.as_of ? ` as of ${fx.as_of}` : '';
  const source = fx?.source || 'Frankfurter';
  const stale = fx?.stale
    ? ' Showing the last saved rate because the live feed was unavailable.'
    : '';
  return `Retell and OpenTable costs are converted from USD (${pairs}${dated}, ${source}).${stale}`;
};

export const formatMinutes = (minutes: NullableNumber) =>
  numberValue(minutes).toLocaleString(undefined, {
    maximumFractionDigits: 1,
  });

export const formatCalls = (count: NullableNumber) =>
  `${numberValue(count).toLocaleString()} calls`;

export const unbilledDescription = (count: number) =>
  count
    ? `${count} store${count === 1 ? '' : 's'} used call minutes in the last 30 days without an invoice being issued`
    : 'Stores that used call minutes in the last 30 days without an invoice being issued';
